/**
 * 前端统一 API 客户端（CLAUDE.md §前端规范 §API 服务层）。
 *
 * - 拦截 401/网络错误并抛出标准化异常。
 * - 自动注入 Authorization: Bearer <token>（从 localStorage 读取；
 *   2026-10-28 起由 sessionStorage 改 localStorage：关浏览器/标签页不掉线，
 *   免登录时长由服务端 30 天 TTL 决定，见 AuthSessionStore.DEFAULT_TTL_SECONDS）。
 * - 自动注入 X-Trace-Id。
 * - 返回后端统一的信封：``{ status, data, message, trace_id, agent_hint, ... }``。
 */

const TOKEN_KEY = "ad_tech_token";
const TRACE_HEADER = "X-Trace-Id";

function uuid(): string {
  // 简单的 v4 UUID（前端不依赖 crypto.randomUUID 的兼容性）
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const rnd = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${rnd()}${rnd()}-${rnd()}-${rnd()}-${rnd()}-${rnd()}${rnd()}${rnd()}`;
}

export interface ApiEnvelope<T> {
  status: "ok" | "error" | string;
  data: T | null;
  message?: string;
  trace_id: string;
  agent_hint?: string;
  suggestion?: string;
  requires_confirmation?: boolean;
  action_id?: string | null;
}

export class ApiError extends Error {
  readonly traceId: string;
  readonly suggestion: string | undefined;
  readonly statusCode: number;

  constructor(message: string, traceId: string, suggestion?: string, statusCode = 400) {
    super(message);
    this.name = "ApiError";
    this.traceId = traceId;
    this.suggestion = suggestion;
    this.statusCode = statusCode;
  }
}

export function setToken(token: string | null): void {
  if (token === null) {
    localStorage.removeItem(TOKEN_KEY);
  } else {
    localStorage.setItem(TOKEN_KEY, token);
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  traceId?: string;
  signal?: AbortSignal;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const url = new URL(path, window.location.origin);
  if (opts.query) {
    Object.entries(opts.query).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      url.searchParams.set(k, String(v));
    });
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const traceId = opts.traceId ?? uuid();
  headers[TRACE_HEADER] = traceId;

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(url.toString(), {
    method,
    headers,
    body,
    signal: opts.signal,
    credentials: "same-origin",
  });

  let payload: ApiEnvelope<T> | null = null;
  try {
    payload = (await res.json()) as ApiEnvelope<T>;
  } catch (e) {
    throw new ApiError(
      `响应解析失败：HTTP ${res.status} ${res.statusText}`,
      traceId,
      undefined,
      res.status,
    );
  }

  if (!payload) {
    throw new ApiError("空响应", traceId, undefined, res.status);
  }
  if (payload.status !== "ok") {
    throw new ApiError(
      payload.message || payload.agent_hint || payload.suggestion || "请求失败",
      payload.trace_id || traceId,
      payload.suggestion,
      res.status,
    );
  }
  return (payload.data ?? (null as unknown)) as T;
}